import { create } from 'zustand';
import type { Workspace } from '@/types';
import * as workspaceService from '@/services/workspace.service';

interface WorkspaceState {
  workspaces: Workspace[];
  activeWorkspace: Workspace | null;
  isLoading: boolean;

  fetchWorkspaces: () => Promise<void>;
  createWorkspace: (
    name: string,
    templateId: string,
    projectId: string
  ) => Promise<Workspace>;
  selectWorkspace: (workspace: Workspace | null) => void;
  startWorkspace: (id: string) => Promise<void>;
  stopWorkspace: (id: string) => Promise<void>;
  deleteWorkspace: (id: string) => Promise<void>;
}

export const useWorkspaceStore = create<WorkspaceState>((set, _get) => ({
  workspaces: [],
  activeWorkspace: null,
  isLoading: false,

  fetchWorkspaces: async () => {
    set({ isLoading: true });
    try {
      const workspaces = await workspaceService.listWorkspaces();
      set({ workspaces });
    } finally {
      set({ isLoading: false });
    }
  },

  createWorkspace: async (name, templateId, projectId) => {
    set({ isLoading: true });
    try {
      const workspace = await workspaceService.createWorkspace(
        name,
        templateId,
        projectId
      );
      set((s) => ({ workspaces: [...s.workspaces, workspace] }));
      return workspace;
    } finally {
      set({ isLoading: false });
    }
  },

  selectWorkspace: (workspace) => {
    set({ activeWorkspace: workspace });
  },

  startWorkspace: async (id) => {
    const updated = await workspaceService.startWorkspace(id);
    set((s) => ({
      workspaces: s.workspaces.map((w) => (w.id === id ? updated : w)),
      activeWorkspace: s.activeWorkspace?.id === id ? updated : s.activeWorkspace,
    }));
  },

  stopWorkspace: async (id) => {
    const updated = await workspaceService.stopWorkspace(id);
    set((s) => ({
      workspaces: s.workspaces.map((w) => (w.id === id ? updated : w)),
      activeWorkspace: s.activeWorkspace?.id === id ? updated : s.activeWorkspace,
    }));
  },

  deleteWorkspace: async (id) => {
    await workspaceService.deleteWorkspace(id);
    set((s) => ({
      workspaces: s.workspaces.filter((w) => w.id !== id),
      activeWorkspace: s.activeWorkspace?.id === id ? null : s.activeWorkspace,
    }));
  },
}));
